import React, { useState, useEffect } from 'react';
import { usePushNotifications } from './usePushNotifications';

export function PushPermissionPrompt({ userEmail, onDismiss }) {
  const { isSupported, permission, subscribe } = usePushNotifications(userEmail);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!isSupported || permission === 'granted' || permission === 'denied' || done || localStorage.getItem('apptnote_push_prompted')) {
    return null;
  }

  const handleEnable = async () => {
    localStorage.setItem('apptnote_push_prompted', 'true');
    setLoading(true);
    const success = await subscribe();
    setLoading(false);
    if (success) setDone(true);
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardIcon}>🔔</div>
      <div style={styles.cardContent}>
        <p style={styles.cardTitle}>Enable daily reminders?</p>
        <p style={styles.cardBody}>
          Get a gentle nudge each evening so logging becomes a habit.
        </p>
        <div style={styles.cardActions}>
          <button
            style={loading ? { ...styles.enableBtn, opacity: 0.7 } : styles.enableBtn}
            onClick={handleEnable}
            disabled={loading}
          >
            {loading ? 'Enabling...' : 'Yes, remind me'}
          </button>
          <button style={styles.dismissBtn} onClick={() => {
            localStorage.setItem('apptnote_push_prompted', 'true');
            onDismiss();
          }}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem('apptnote_install_dismissed')) return;

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const isSafari = /safari/.test(navigator.userAgent.toLowerCase()) &&
                     !/chrome/.test(navigator.userAgent.toLowerCase());

    if (ios && isSafari) {
      setIsIOS(true);
      setTimeout(() => setShow(true), 4000);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 4000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem('apptnote_install_dismissed', 'true');
  };

  if (!show) return null;

  return (
    <div style={styles.installBanner}>
      <span style={styles.installIcon}>🌿</span>
      <div style={styles.installText}>
        {isIOS ? (
          <>
            <strong>Add ApptNote to your Home Screen</strong>
            <p>Tap <strong>Share ⎙</strong> → <strong>Add to Home Screen</strong></p>
          </>
        ) : (
          <>
            <strong>Add ApptNote to your Home Screen</strong>
            <p>Log in one tap and get daily reminders</p>
          </>
        )}
      </div>
      <div style={styles.installActions}>
        {!isIOS && (
          <button style={styles.addBtn} onClick={handleInstall}>Add</button>
        )}
        <button style={styles.closeBannerBtn} onClick={dismiss}>✕</button>
      </div>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    backgroundColor: '#fff8f2',
    border: '1.5px solid #D4A5A5',
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '20px',
  },
  cardIcon: {
    fontSize: '24px',
    flexShrink: 0,
    marginTop: '2px',
  },
  cardContent: {
    flex: 1,
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#2C2C2A',
    margin: '0 0 4px',
  },
  cardBody: {
    fontSize: '14px',
    color: '#4a6070',
    lineHeight: '1.5',
    margin: '0 0 14px',
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  enableBtn: {
    backgroundColor: '#7A9E87',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    color: '#6c8396',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0',
    fontFamily: 'inherit',
  },
  installBanner: {
    position: 'fixed',
    bottom: '20px',
    left: '16px',
    right: '16px',
    backgroundColor: '#2C2C2A',
    borderRadius: '16px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 8px 32px rgba(44,44,42,0.25)',
    zIndex: 999,
    maxWidth: '500px',
    margin: '0 auto',
  },
  installIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  installText: {
    flex: 1,
    color: 'white',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  installActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexShrink: 0,
  },
  addBtn: {
    backgroundColor: '#7A9E87',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  closeBannerBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
  },
};